/**
 * VA Dashboard — Interview Section
 *
 * Shows the VA's interview history, pending employer requests,
 * and the "Start AI Interview" button.
 * Designed to be embedded inside the VA Dashboard page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Video, Play, Clock,
  Bell, Loader2, AlertCircle, Trash2, Globe, EyeOff
} from 'lucide-react';
import AIInterviewModal from '../../../components/interview/AIInterviewModal';
import AIVideoInterviewSection from '../../va-profile/components/AIVideoInterviewSection';
import aiInterviewService from '../../../services/aiInterviewService';
import { useAuth } from '../../../hooks/useAuth';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDuration(secs) {
  if (!secs) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

const InterviewSection = () => {
  const { user, userProfile } = useAuth();

  const [interviews,      setInterviews]      = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [showModal,       setShowModal]       = useState(false);
  const [viewingSession,  setViewingSession]  = useState(null);
  const [activeRequest,   setActiveRequest]   = useState(null); // employer-requested context
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [togglingPublicId,  setTogglingPublicId]  = useState(null);
  const [confirmDeleteId,   setConfirmDeleteId]   = useState(null);

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [interviewList, requestList] = await Promise.all([
        aiInterviewService.getVAInterviews(user.id),
        aiInterviewService.getPendingRequestsForVA(user.id),
      ]);
      setInterviews(interviewList);
      setPendingRequests(requestList);
    } catch (err) {
      setError('Failed to load interview data. Please refresh.');
      console.error('[InterviewSection] loadData error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── One-time storage path migration (UUID folder → full-name slug) ──────────
  // Runs silently in the background on first visit after the rename update.
  useEffect(() => {
    if (!user?.id || !userProfile?.full_name) return;
    const key = `interview_storage_migrated_${user.id}`;
    if (localStorage.getItem(key)) return; // already done on this device

    aiInterviewService
      .migrateStoragePaths(user.id, userProfile.full_name)
      .then(({ migrated, skipped, errors }) => {
        if (migrated > 0 || errors > 0) {
          console.info(`[InterviewSection] Storage migration: ${migrated} migrated, ${skipped} skipped, ${errors} errors`);
        }
        localStorage.setItem(key, '1');
      })
      .catch((err) => console.warn('[InterviewSection] Storage migration failed:', err.message));
  }, [user?.id, userProfile?.full_name]);

  const handleInterviewComplete = () => {
    setShowModal(false);
    setActiveRequest(null);
    loadData();
  };

  const handleStartFromRequest = (request) => {
    setActiveRequest(request);
    setShowModal(true);
  };

  const handleDeleteInProgressSession = (sessionId, e) => {
    e.stopPropagation();
    setConfirmDeleteId(sessionId);
  };

  const handleConfirmDelete = async () => {
    const sessionId = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      setDeletingSessionId(sessionId);
      await aiInterviewService.deleteInterview(sessionId);
      setInterviews(prev => prev.filter(s => s.id !== sessionId));
    } catch (err) {
      console.error('[InterviewSection] Error deleting session:', err);
      setError('Failed to delete the interview session. Please try again.');
    } finally {
      setDeletingSessionId(null);
    }
  };

  const handleTogglePublic = async (sessionId, currentIsPublic, e) => {
    e.stopPropagation();
    try {
      setTogglingPublicId(sessionId);
      await aiInterviewService.setInterviewPublic(sessionId, !currentIsPublic);
      setInterviews(prev =>
        prev.map(s => s.id === sessionId ? { ...s, is_public: !currentIsPublic } : s)
      );
    } catch (err) {
      console.error('[InterviewSection] Error toggling public:', err);
      setError('Failed to update interview visibility. Please try again.');
    } finally {
      setTogglingPublicId(null);
    }
  };

  const latestCompleted = interviews.find(i => i.status === 'completed');

  return (
    <section className="space-y-5 p-6 bg-white rounded-xl shadow-md border border-gray-200">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Video className="w-4 h-4 text-emerald-600" />
            Take our AI interview for a chance to shine to employers!
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Record a personalized interview to showcase your skills to employers.
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Note: Only employers can view the interviews.
          </p>
        </div>
        <button
          onClick={() => { setActiveRequest(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
        >
          <Play className="w-4 h-4" />
          <span className="hidden sm:inline">{latestCompleted ? 'Retake Interview' : 'Start Interview'}</span>
          <span className="sm:hidden">{latestCompleted ? 'Retake' : 'Start'}</span>
        </button>
      </div>

      {/* ── Pending employer requests ── */}
      {pendingRequests.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Pending Requests
          </p>
          {pendingRequests.map(req => (
            <div
              key={req.id}
              className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl p-4"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full overflow-hidden bg-amber-100 flex items-center justify-center shrink-0">
                  {req.employer?.profile_picture ? (
                    <img src={req.employer.profile_picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Bell className="w-4 h-4 text-amber-600" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    {req.employer?.full_name || 'An employer'} requested an interview
                  </p>
                  {req.job?.title && (
                    <p className="text-xs text-gray-500 mt-0.5">for <strong>{req.job.title}</strong></p>
                  )}
                  {req.message && (
                    <p className="text-xs text-gray-500 mt-1 italic">"{req.message}"</p>
                  )}
                  <p className="text-xs text-amber-600 mt-1">
                    <Clock className="w-3 h-3 inline mr-0.5" />
                    Expires {formatDate(req.expires_at)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleStartFromRequest(req)}
                className="ml-4 shrink-0 flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 transition-colors"
              >
                <Play className="w-3.5 h-3.5" />
                Start
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Body ── */}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
          <div>
            <p className="text-sm text-red-700">{error}</p>
            <button onClick={loadData} className="text-xs text-red-600 underline mt-1 hover:no-underline">
              Try again
            </button>
          </div>
        </div>
      ) : interviews.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
          <Video className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700 mb-1">No interviews yet</p>
          <p className="text-xs text-gray-400 mb-4">
            Start your first AI interview and stand out to employers.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
          >
            Start My First Interview
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {interviews.map(session => {
            const isCompleted = session.status === 'completed';

            return (
              <div
                key={session.id}
                className="bg-white border border-gray-200 rounded-xl p-4 hover:border-emerald-200 transition-colors cursor-pointer"
                onClick={() => isCompleted && setViewingSession(session.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isCompleted ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                      {isCompleted ? (
                        <Video className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        {isCompleted ? 'Completed Interview' : 'In Progress'}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDate(session.created_at)}
                        {session.video_duration_secs > 0 && ` · ${formatDuration(session.video_duration_secs)}`}
                        {isCompleted && ` · ${session.rounds_completed}/${session.total_rounds} rounds`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Make Public / Remove from Profile toggle (completed only) */}
                    {isCompleted && (
                      <button
                        onClick={(e) => handleTogglePublic(session.id, !!session.is_public, e)}
                        disabled={togglingPublicId === session.id}
                        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          session.is_public
                            ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                        title={session.is_public ? 'Remove from public profile' : 'Publish to public profile'}
                      >
                        {togglingPublicId === session.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : session.is_public ? (
                          <><EyeOff className="w-3.5 h-3.5" /><span className="hidden sm:inline">Unpublish</span></>
                        ) : (
                          <><Globe className="w-3.5 h-3.5" /><span className="hidden sm:inline">Publish</span></>
                        )}
                      </button>
                    )}
                    <button
                      onClick={(e) => handleDeleteInProgressSession(session.id, e)}
                      disabled={deletingSessionId === session.id}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-gray-400 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete interview"
                      aria-label="Delete interview"
                    >
                      {deletingSessionId === session.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── View session modal (inline – uses InterviewVideoPlayer) ── */}
      {viewingSession && (
        <SessionDetailOverlay
          sessionId={viewingSession}
          onClose={() => setViewingSession(null)}
          vaName={userProfile?.full_name}
        />
      )}

      {/* ── Delete Confirmation Modal ── */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50" onClick={() => setConfirmDeleteId(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-5">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Delete Interview</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Are you sure you want to delete this interview? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── AI Interview Modal ── */}
      <AIInterviewModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setActiveRequest(null); }}
        vaProfile={userProfile}
        jobContext={activeRequest?.job ? { id: activeRequest.job_id, title: activeRequest.job.title } : null}
        employerId={activeRequest?.employer_id || null}
        onInterviewComplete={handleInterviewComplete}
      />
    </section>
  );
};

// Small overlay to view a completed session
function SessionDetailOverlay({ sessionId, onClose, vaName }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    aiInterviewService.getSession(sessionId)
      .then(setSession)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [sessionId]);

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/60" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">Interview Recording</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">&times;</button>
        </div>
        <div>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : session ? (
            <AIVideoInterviewSection
              interview={session}
              vaName={vaName}
            />
          ) : (
            <p className="text-gray-500 text-sm text-center py-8">Session not found.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default InterviewSection;
